import { COLLECTIONS, money, pricingText } from "@/components/supply/catalogData";

export const Opt = ({ active, onClick, children, testId }) => (
  <button type="button" onClick={onClick} data-testid={testId}
          className="font-label text-[0.65rem] px-4 py-2 border text-left min-h-[44px]"
          style={{ borderColor: active ? "var(--ss-rust)" : "var(--ss-border)", background: active ? "var(--ss-rust)" : "transparent", color: active ? "#F5F1E8" : "var(--ss-cream)" }}>
    {children}
  </button>
);

export const PackagePicker = ({ cat, value, set }) => {
  const pick = (k) => set({ package: value.package === k ? "" : k, package_addons: [], package_2xl: 0 });
  const pkg = cat.packages.find((p) => p.key === value.package);
  const hasBackdrop = !!pkg && pkg.includes.some((i) => /backdrop/i.test(i));
  const toggleAddon = (a) => {
    let next = value.package_addons.includes(a.key) ? value.package_addons.filter((x) => x !== a.key) : [...value.package_addons, a.key];
    if (a.group && next.includes(a.key)) next = next.filter((x) => x === a.key || pkg.addons.find((y) => y.key === x)?.group !== a.group);
    set({ package_addons: next });
  };
  return (
    <div className="space-y-4" data-testid="supply-package-picker">
      <div className="font-label text-[0.6rem]" style={{ color: "var(--ss-gold)" }}>Packages (optional)</div>
      {COLLECTIONS.map(([c, title]) => (
        <div key={c}>
          <div className="text-xs mb-2" style={{ color: "var(--ss-muted)" }}>{title}</div>
          <div className="flex flex-wrap gap-2">
            {cat.packages.filter((p) => p.collection === c).map((p) => (
              <Opt key={p.key} active={value.package === p.key} onClick={() => pick(p.key)} testId={`supply-pkg-${p.key}`}>
                {p.label} · ${p.price.toLocaleString()}
              </Opt>
            ))}
          </div>
        </div>
      ))}
      {pkg && (
        <div className="p-4 border text-sm" style={{ borderColor: "var(--ss-border)" }} data-testid="supply-pkg-detail">
          <div className="font-semibold">{pkg.label} — ${pkg.price.toLocaleString()} · minimum {pkg.lead} calendar days</div>
          <ul className="mt-2 grid sm:grid-cols-2 gap-x-6 text-xs" style={{ color: "var(--ss-cream-dim)" }}>
            {pkg.includes.map((it) => <li key={it}>• {it}</li>)}
          </ul>
          {pkg.collection === "graduation" && !hasBackdrop && <p className="mt-2 text-xs" style={{ color: "var(--ss-muted)" }} data-testid="supply-pkg-no-backdrop">Backdrop not included. Adding a custom backdrop changes the minimum lead time for the entire order to 21 days.</p>}
          {pkg.key === "grad_signature" && <p className="mt-2 text-xs" style={{ color: "var(--ss-muted)" }} data-testid="supply-pkg-backdrop-included">6×4 custom backdrop included.</p>}
          {pkg.collection === "executive" && <p className="mt-2 text-xs" style={{ color: "var(--ss-muted)" }}>Graduation stole not included. Set your Event Date at the top of the form. Delivered and set up at your event with the nightclub. One Executive Graduation Experience per weekend; subject to nightclub availability on your event date. Paid 100% at signing.</p>}
          {pkg.collection === "birthday" && <p className="mt-2 text-xs" style={{ color: "var(--ss-muted)" }}>Includes one coordinated theme/design concept. Children's T-shirts are not included — add any à la carte product below at regular pricing.</p>}
          {pkg.shirts > 0 && (
            <label className="mt-3 flex flex-wrap items-center gap-2 text-xs">
              Need a 2XL+ shirt? +$5 each{pkg.shirts > 1 ? ` (applies to each of the ${pkg.shirts} included S–XL shirts changed to 2XL+). How many?` : ". How many?"}
              <select className="editorial-input text-sm min-h-[44px]" style={{ width: "5rem" }} value={value.package_2xl || 0}
                      onChange={(e) => set({ package_2xl: Number(e.target.value) })} data-testid="supply-pkg-2xl">
                {Array.from({ length: pkg.shirts + 1 }).map((_, i) => <option key={i} value={i}>{i}</option>)}
              </select>
            </label>
          )}
          {pkg.addons.length > 0 && (
            <div className="mt-4" data-testid="supply-optional-addons">
              <div className="font-label text-[0.6rem] mb-2" style={{ color: "var(--ss-gold)" }}>Optional Add-Ons</div>
              {pkg.addons.some((a) => a.group === "backdrop") && (
                <div className="mb-3">
                  <p className="text-xs mb-1">6×4 custom backdrop included.</p>
                  <p className="text-xs mb-2" style={{ color: "var(--ss-muted)" }}>Optional backdrop upgrades:</p>
                  <div className="flex flex-wrap gap-2">
                    {pkg.addons.filter((a) => a.group === "backdrop").map((a) => (
                      <Opt key={a.key} active={value.package_addons.includes(a.key)} onClick={() => toggleAddon(a)} testId={`supply-addon-${a.key}`}>
                        Upgrade to {a.label.split(" to ")[1]}: +{money(a.price)}
                      </Opt>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                {pkg.addons.filter((a) => a.group !== "backdrop").map((a) => (
                  <Opt key={a.key} active={value.package_addons.includes(a.key)} onClick={() => toggleAddon(a)} testId={`supply-addon-${a.key}`}>
                    {a.label} · +{money(a.price)}
                  </Opt>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ItemRow = ({ p, item, setItem }) => {
  const on = !!item;
  return (
    <div className="py-2 border-b flex flex-wrap items-center gap-3 text-sm" style={{ borderColor: "var(--ss-border)" }} data-testid={`supply-item-${p.key}`}>
      <label className="flex items-center gap-3 flex-1 min-w-[12rem] min-h-[44px] cursor-pointer">
        <input type="checkbox" className="w-5 h-5 shrink-0" checked={on} onChange={() => setItem(on ? null : { key: p.key, quantity: p.quantities[0] || 0, lights: false })} data-testid={`supply-item-check-${p.key}`} />
        <span>{p.label} <span style={{ color: "var(--ss-muted)" }}>· {pricingText(p)}</span></span>
      </label>
      {on && p.quantities.length > 0 && p.pricing.type !== "each" && (
        <select className="editorial-input text-sm" style={{ width: "6rem" }} value={item.quantity}
                onChange={(e) => setItem({ ...item, quantity: Number(e.target.value) })} data-testid={`supply-item-qty-${p.key}`}>
          {p.quantities.map((q) => <option key={q} value={q}>{q}</option>)}
        </select>
      )}
      {on && ["quote", "each"].includes(p.pricing.type) && (
        <input type="number" min="1" className="editorial-input text-sm" style={{ width: "6rem" }} placeholder="Qty" value={item.quantity || ""}
               onChange={(e) => setItem({ ...item, quantity: Number(e.target.value) || 0 })} data-testid={`supply-item-qty-${p.key}`} />
      )}
      {on && p.lights && (
        <select className="editorial-input text-sm" style={{ width: "13rem" }} value={item.lights ? "yes" : "no"}
                onChange={(e) => setItem({ ...item, lights: e.target.value === "yes" })} data-testid={`supply-item-lights-${p.key}`} aria-label="Add Flashing Lights?">
          <option value="no">Flashing lights: No</option><option value="yes">Flashing lights: Yes, +$3 per cup</option>
        </select>
      )}
      {p.note && <div className="w-full text-xs" style={{ color: "var(--ss-muted)" }}>{p.note}</div>}
    </div>
  );
};

export const ItemPicker = ({ cat, value, set }) => {
  const setItem = (key, it) => {
    const rest = value.items.filter((x) => x.key !== key);
    set({ items: it ? [...rest, it] : rest });
  };
  return (
    <div className="space-y-6" data-testid="supply-item-picker">
      <div className="font-label text-[0.6rem]" style={{ color: "var(--ss-gold)" }}>À la carte products (choose as many as you like)</div>
      {cat.sections.map((s) => (
        <div key={s.key}>
          <div className="text-xs font-semibold mb-1">{s.title}</div>
          {s.note && <p className="text-xs mb-2" style={{ color: "var(--ss-muted)" }}>{s.note}</p>}
          {cat.products.filter((p) => p.section === s.key).map((p) => (
            <ItemRow key={p.key} p={p} item={value.items.find((x) => x.key === p.key)} setItem={(it) => setItem(p.key, it)} />
          ))}
        </div>
      ))}
    </div>
  );
};
